import { Component, OnInit } from '@angular/core'
import { ActivatedRoute } from '@angular/router'
import { Location } from '@angular/common'
import { combineLatest, Observable } from 'rxjs'
import { DealMutationService } from '../../../../services/dealMutation.service'
import { filter, map, mapTo, mergeMap, pluck, share, take, tap } from 'rxjs/operators'
import { DealConfigurationQuery } from '../../../../../../generated/graphql'
import { ConfigurationRoute, ConfigurationRouteService } from '../../services/configurationRoute.service'
import { LocaleService } from '../../../../../../framework/locale/services/locale.service'
import { ErrorService } from '../../../../../../ui/monolith/services/error.service'
import { ConfigurationAnalyticsService } from '../../services/configurationAnalytics.service'
import { ReferralService } from 'framework/referral/referral.service'
import { DealQueryService } from 'modules/deals/services/dealQuery.service'
import { ModulePathService } from 'framework/module-path-service/module-path.service'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { ActionBannerService } from 'modules/deals/configurator/services/action-banner.service'
import { EnvironmentService } from 'framework/environment/environment.service'
import { SubsidyBannerService } from 'modules/deals/configurator/services/sepp-subsidy-banner.service'

@UntilDestroy()
@Component({
    selector: 'mg-layout',
    templateUrl: './layout.component.html',
    styleUrls: ['./layout.component.scss'],
})
export class LayoutComponent implements OnInit {
    configurationId: string
    accordionRoutes$: Observable<ConfigurationRoute[]>
    isUpdating$: Observable<boolean>
    isInitialized$: Observable<boolean>
    productConfiguration$: Observable<DealConfigurationQuery['Deal']>
    isReferralCodeChecked: boolean = false
    route$: Observable<ConfigurationRoute>
    modelName$: Observable<string>
    subsidyShowBanner: boolean
    subsidyBannerContent: string
    subsidyBannerColor: string
    actionBannerContent: string
    actionBannerColor: string

    constructor(
        private route: ActivatedRoute,
        private location: Location,
        private dealQueryService: DealQueryService,
        private dealMutationService: DealMutationService,
        private configurationRouteService: ConfigurationRouteService,
        public localeService: LocaleService,
        private errorService: ErrorService,
        private configurationAnalyticsService: ConfigurationAnalyticsService,
        private referralService: ReferralService,
        private modulePathService: ModulePathService,
        private subsidyBannerService: SubsidyBannerService,
        private actionBannerService: ActionBannerService,
        private environmentService: EnvironmentService
    ) {}

    get locale() {
        return this.localeService.locale
    }

    ngOnInit() {
        const configurationId$ = this.route.data.pipe(
            //it is assuming that configurationId is going to be there, but in many cases it's probably not there, however in HTML file it is being checked. My guess configurationId variable should be only observable?
            untilDestroyed(this),
            pluck('configurationId'),
            filter(Boolean),
            tap<string>(configurationId => (this.configurationId = configurationId))
        )

        combineLatest([this.referralService.referralCode$, configurationId$])
            .pipe(untilDestroyed(this))
            .subscribe(([referralCode, id]) => {
                if (!this.isReferralCodeChecked && referralCode) {
                    this.dealMutationService.updateDealReferralCode(id, referralCode).then(() => {
                        this.isReferralCodeChecked = true
                    })
                }
            })

        // available routes for layout
        this.accordionRoutes$ = configurationId$.pipe(
            untilDestroyed(this),
            mergeMap(configurationId => this.configurationRouteService.getConfigurationRoutes(configurationId)),
            map(routes => routes.filter(route => route.isAccordionStep))
        )

        // currently active route
        this.route$ = configurationId$.pipe(
            untilDestroyed(this),
            mergeMap(configurationId => this.configurationRouteService.getActiveConfigurationRoute(configurationId)),
            share()
        )

        // updating state of configuration
        this.isUpdating$ = this.dealMutationService.isUpdating.pipe(untilDestroyed(this))

        this.productConfiguration$ = configurationId$.pipe(
            untilDestroyed(this),
            mergeMap(id => this.dealQueryService.watchDealConfiguration(id)),
            this.errorService.observableCatcher()
        )

        this.productConfiguration$.pipe(untilDestroyed(this)).subscribe(deal => {
            let { content, shouldShowContent, color } = this.subsidyBannerService.retrieveSubsidyBannerDetails(deal)

            this.subsidyBannerContent = content
            this.subsidyBannerColor = color
            this.subsidyShowBanner = shouldShowContent
        })

        this.modelName$ = this.productConfiguration$.pipe(
            untilDestroyed(this),
            pluck<DealConfigurationQuery['Deal'], string>('modelName')
        )

        this.dealMutationService.optionUpdated.pipe(untilDestroyed(this)).subscribe(event => {
            this.configurationAnalyticsService.push({
                ...(event.deal as DealConfigurationQuery['Deal']),
                name: event.name,
                dealId: (event.deal as DealConfigurationQuery['Deal']).id,
            })
        })

        this.isInitialized$ = combineLatest([this.accordionRoutes$, this.modelName$]).pipe(
            untilDestroyed(this),
            take(1),
            mapTo(true)
        )

        const actionBannerDetails = this.actionBannerService.retrieveBannerDetails()
        this.actionBannerContent = actionBannerDetails.mainContent
        this.actionBannerColor = actionBannerDetails.color
    }

    isCurrentPath(path) {
        const location = this.modulePathService.getPathFromModuleRoot(this.location.path().split('?')[0])
        path = this.location.normalize(path)

        return location === path
    }
}
